import type { ReactNode } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  DefaultTooltipContent,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { CostChartViewMode } from '../../costs/costChartTypes';
import { formatCrawlJobTimeSeriesXAxisLabel } from '../crawlJobTimeSeriesChart';
import { chartColorAt } from '../../../utils/chartColors';
import {
  formatCrawlJobPeriodLabel,
  LAC_CRAWL_JOB_TIME_SERIES_CHART_HEIGHT,
} from './lacCrawlJobTimeSeriesChartModel';

type LacCrawlJobTimeSeriesChartPlotProps = {
  isLoading: boolean;
  isError: boolean;
  chartData: Array<Record<string, string | number>>;
  stackedByTenant: boolean;
  tenants: string[];
  resolvedTenantLabels: Record<string, string>;
  yMax: number | undefined;
  mode: CostChartViewMode;
  locale: string;
  countLabel: string;
  loadingLabel: string;
  loadErrorLabel: string;
  emptyLabel: string;
};

export function LacCrawlJobTimeSeriesChartPlot({
  isLoading,
  isError,
  chartData,
  stackedByTenant,
  tenants,
  resolvedTenantLabels,
  yMax,
  mode,
  locale,
  countLabel,
  loadingLabel,
  loadErrorLabel,
  emptyLabel,
}: LacCrawlJobTimeSeriesChartPlotProps): ReactNode {
  if (isLoading) {
    return <p className="cost-chart__status">{loadingLabel}</p>;
  }
  if (isError) {
    return (
      <p className="cost-chart__status" role="alert">
        {loadErrorLabel}
      </p>
    );
  }
  if (chartData.length === 0) {
    return <p className="cost-chart__status">{emptyLabel}</p>;
  }

  return (
    <ResponsiveContainer
      width="100%"
      height={LAC_CRAWL_JOB_TIME_SERIES_CHART_HEIGHT}
    >
      <ComposedChart
        data={chartData}
        margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--color-border)"
          vertical={false}
        />
        <XAxis
          dataKey="label"
          tickFormatter={(value) =>
            formatCrawlJobTimeSeriesXAxisLabel(value, mode)
          }
          tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tickFormatter={(value) =>
            new Intl.NumberFormat(locale, {
              maximumFractionDigits: 0,
              notation: 'compact',
            }).format(value)
          }
          tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
          axisLine={false}
          tickLine={false}
          width={40}
          domain={[0, yMax ?? 'auto']}
        />
        <Tooltip
          wrapperStyle={{ zIndex: 'var(--z-tooltip)' }}
          formatter={(value, name) => [
            new Intl.NumberFormat(locale).format(Number(value)),
            stackedByTenant
              ? (resolvedTenantLabels[String(name)] ?? String(name))
              : countLabel,
          ]}
          labelFormatter={(label) =>
            formatCrawlJobPeriodLabel(String(label), mode, locale)
          }
          content={(props: TooltipContentProps) => {
            if (!props.active) return null;
            const payload = props.payload.filter(
              (entry) => Number(entry.value) > 0,
            );
            if (payload.length === 0) return null;
            return <DefaultTooltipContent {...props} payload={payload} />;
          }}
          cursor={{ fill: 'var(--color-border)', opacity: 0.5 }}
        />
        {stackedByTenant ? (
          tenants.map((tenant, index) => (
            <Bar
              key={tenant}
              dataKey={tenant}
              name={resolvedTenantLabels[tenant] ?? tenant}
              stackId="crawlJobs"
              fill={chartColorAt(index)}
              radius={
                index === tenants.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]
              }
              maxBarSize={40}
            />
          ))
        ) : (
          <Bar
            dataKey="count"
            name={countLabel}
            fill="var(--color-accent)"
            radius={[3, 3, 0, 0]}
            maxBarSize={40}
          />
        )}
        {stackedByTenant && tenants.length > 0 ? (
          <Legend
            verticalAlign="bottom"
            height={28}
            iconType="square"
            iconSize={10}
            wrapperStyle={{
              fontSize: '0.75rem',
              color: 'var(--color-text-muted)',
            }}
          />
        ) : null}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
